import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { addDays, format } from 'date-fns';
import { expandCustodySchedule, applyOverrides, buildFullCustodyMap } from '@coparent/shared/src/utils/custodyUtils';
import { expandActivityOccurrences } from '@coparent/shared/src/utils/activityUtils';
import {
  findOrCreateCoparentCalendar,
  createEvent,
  updateEvent,
  deleteEvent,
  batchDeleteEvents
} from './googleCalendarService';

const SYNC_WINDOW_DAYS = 90;

// --- Firestore event map helpers ---

const getEventMapRef = (userId) => doc(db, 'users', userId, 'googleCalendarSync', 'eventMap');

const getEventMap = async (userId) => {
  const snap = await getDoc(getEventMapRef(userId));
  return snap.exists() ? snap.data() : {};
};

const saveEventMap = async (userId, map) => {
  await setDoc(getEventMapRef(userId), map);
};

// --- Helpers ---

const getParentLabel = (family, parentId) => {
  const label = family.settings?.parentLabels?.[parentId];
  if (label) return label;
  return family.parentProfiles?.[parentId]?.displayName || 'Parent';
};

const nextDay = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return format(addDays(d, 1), 'yyyy-MM-dd');
};

const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// --- Build Google Calendar event objects ---

const buildCustodyGoogleEvent = (dateStr, parentIndex, family) => {
  const parentId = family.parentIds[parentIndex - 1];
  const label = getParentLabel(family, parentId);

  return {
    summary: `${label}'s day`,
    start: { date: dateStr },
    end: { date: nextDay(dateStr) },
    description: 'Managed by coparent',
    transparency: 'transparent',
  };
};

const buildActivityGoogleEvent = (activity, dateStr) => {
  const tz = getTimezone();
  const event = {
    summary: activity.name,
    description: 'Managed by coparent',
  };

  if (activity.startTime) {
    event.start = { dateTime: `${dateStr}T${activity.startTime}:00`, timeZone: tz };
    if (activity.endTime) {
      event.end = { dateTime: `${dateStr}T${activity.endTime}:00`, timeZone: tz };
    } else {
      // Default 1-hour duration
      const [h, m] = activity.startTime.split(':').map(Number);
      const endH = String(h + 1).padStart(2, '0');
      const endM = String(m).padStart(2, '0');
      event.end = { dateTime: `${dateStr}T${endH}:${endM}:00`, timeZone: tz };
    }
  } else {
    // All-day event
    event.start = { date: dateStr };
    event.end = { date: nextDay(dateStr) };
  }

  if (activity.location) {
    event.location = activity.location;
  }

  return event;
};

// --- Sync functions ---

/**
 * Sync custody events to Google Calendar for the next 90 days
 */
export const syncCustodyEvents = async (userId, calendarId, schedules, overrides, family) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = addDays(today, SYNC_WINDOW_DAYS);

  const merged = buildFullCustodyMap(schedules, overrides, family, today, endDate);
  const eventMap = await getEventMap(userId);

  const newMap = { ...eventMap };
  const processedKeys = new Set();

  // Create or update events
  for (const [dateStr, entry] of Object.entries(merged)) {
    const parentIndex = typeof entry === 'number' ? entry : entry.parentIndex;
    const key = `custody-${dateStr}`;
    processedKeys.add(key);

    const googleEvent = buildCustodyGoogleEvent(dateStr, parentIndex, family);
    const existingGoogleId = eventMap[key];

    if (existingGoogleId) {
      try {
        await updateEvent(calendarId, existingGoogleId, googleEvent);
      } catch (err) {
        // Event may have been deleted externally, recreate
        if (err.message.includes('404') || err.message.includes('Not Found')) {
          const created = await createEvent(calendarId, googleEvent);
          newMap[key] = created.id;
        } else {
          console.warn(`Failed to update custody event ${key}:`, err);
        }
      }
    } else {
      const created = await createEvent(calendarId, googleEvent);
      newMap[key] = created.id;
    }
  }

  // Delete events that are no longer in the schedule
  const toDelete = Object.keys(eventMap)
    .filter(key => key.startsWith('custody-') && !processedKeys.has(key));

  for (const key of toDelete) {
    try {
      await deleteEvent(calendarId, eventMap[key]);
    } catch (err) {
      console.warn(`Failed to delete custody event ${key}:`, err);
    }
    delete newMap[key];
  }

  await saveEventMap(userId, newMap);
};

/**
 * Sync activity events to Google Calendar for the next 90 days
 */
export const syncActivityEvents = async (userId, calendarId, activities) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = addDays(today, SYNC_WINDOW_DAYS);

  const eventMap = await getEventMap(userId);
  const newMap = { ...eventMap };
  const processedKeys = new Set();

  for (const activity of activities) {
    const occurrences = expandActivityOccurrences(activity, today, endDate);

    for (const { date: dateStr } of occurrences) {
      const key = `activity-${activity.id}-${dateStr}`;
      processedKeys.add(key);

      const googleEvent = buildActivityGoogleEvent(activity, dateStr);
      const existingGoogleId = eventMap[key];

      if (existingGoogleId) {
        try {
          await updateEvent(calendarId, existingGoogleId, googleEvent);
        } catch (err) {
          if (err.message.includes('404') || err.message.includes('Not Found')) {
            const created = await createEvent(calendarId, googleEvent);
            newMap[key] = created.id;
          } else {
            console.warn(`Failed to update activity event ${key}:`, err);
          }
        }
      } else {
        const created = await createEvent(calendarId, googleEvent);
        newMap[key] = created.id;
      }
    }
  }

  // Delete activity events no longer in schedule
  const toDelete = Object.keys(eventMap)
    .filter(key => key.startsWith('activity-') && !processedKeys.has(key));

  for (const key of toDelete) {
    try {
      await deleteEvent(calendarId, eventMap[key]);
    } catch (err) {
      console.warn(`Failed to delete activity event ${key}:`, err);
    }
    delete newMap[key];
  }

  await saveEventMap(userId, newMap);
};

/**
 * Full sync based on user's sync mode preference
 */
export const fullSync = async (userId, calendarId, syncMode, { schedules, overrides, family, activities }) => {
  if (syncMode === 'custody' || syncMode === 'both') {
    await syncCustodyEvents(userId, calendarId, schedules, overrides, family);
  }

  if (syncMode === 'activities' || syncMode === 'both') {
    await syncActivityEvents(userId, calendarId, activities);
  }
};

/**
 * Remove all synced events from Google Calendar and clear the event map
 */
export const removeAllSyncedEvents = async (userId, calendarId) => {
  const eventMap = await getEventMap(userId);
  const googleIds = Object.values(eventMap);

  if (googleIds.length > 0) {
    await batchDeleteEvents(calendarId, googleIds);
  }

  await saveEventMap(userId, {});
};
